import React, { useState, useEffect } from 'react';
import { X, Save, Loader, Calendar, Plus, Trash2, AlertTriangle } from 'lucide-react';
import ApiService from './ApiService';
import { usePermissions, PERMISSIONS } from './usePermissions';

const ProtocolAssignment = ({ isOpen, onClose, patient, config, onProtocolAssigned, existingProtocol = null, currentUser }) => {
  const { hasPermission } = usePermissions(currentUser);
  
  const isEditMode = !!existingProtocol;
  const [step, setStep] = useState(existingProtocol ? 2 : 1); // Skip to step 2 if editing
  const [medications, setMedications] = useState([]);
  const [patientMedications, setPatientMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errorModal, setErrorModal] = useState(null);
  
  // Step 1: Medication Selection
  const [selectedMedication, setSelectedMedication] = useState(null);
  
  // Step 2: Protocol Configuration
  const [protocolType, setProtocolType] = useState('daily'); // 'daily', 'weekly', 'multiphase'
  const [protocolName, setProtocolName] = useState('');
  
  // Daily Schedule
  const [dailySchedule, setDailySchedule] = useState({
    timesPerDay: 1,
    duration: '',
    durationUnit: 'weeks', // 'days', 'weeks', 'months'
    doses: [
      {
        time: 'AM',
        dosage: '',
        dosageUnit: '',
        instructions: ''
      }
    ]
  });
  
  // Weekly Schedule
  const [weeklySchedule, setWeeklySchedule] = useState({
    timesPerWeek: 1,
    duration: '',
    durationUnit: 'weeks',
    specificDays: false,
    selectedDays: [],
    dosage: '',
    dosageUnit: '',
    instructions: ''
  });
  
  // Multi-Phase
  const [phases, setPhases] = useState([
    {
      phaseNumber: 1,
      dosage: '',
      dosageUnit: '',
      frequency: 'daily',
      timesPerDay: 1,
      timesPerWeek: 1,
      specificDays: [],
      dailyDoses: [
        {
          time: 'AM',
          dosage: '',
          dosageUnit: ''
        }
      ],
      duration: '',
      durationUnit: 'weeks',
      instructions: ''
    }
  ]);

  const t = config?.terminology || {
    item: { singular: 'Medication', plural: 'Medications', singularLower: 'medication', pluralLower: 'medications' },
    protocol: { singular: 'Protocol', plural: 'Protocols', singularLower: 'protocol', pluralLower: 'protocols' }
  };

  const timeOptions = ['AM', 'Noon', 'PM', 'Bedtime', 'Morning', 'Afternoon', 'Evening', 'Night'];
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    if (isOpen) {
      loadData();
      
      // If editing existing protocol, populate the form
      if (existingProtocol) {
        // Set the medication
        setSelectedMedication({
          medicationId: existingProtocol.medicationId,
          name: existingProtocol.medicationName,
          dosageUnit: existingProtocol.schedule?.doses?.[0]?.dosageUnit || 
                       existingProtocol.schedule?.dosageUnit || 'mg'
        });
        
        // Set protocol name and type
        setProtocolName(existingProtocol.protocolName);
        setProtocolType(existingProtocol.protocolType);
        
        // Populate schedule based on type
        if (existingProtocol.protocolType === 'daily' && existingProtocol.schedule) {
          setDailySchedule({
            timesPerDay: existingProtocol.schedule.timesPerDay || existingProtocol.schedule.doses?.length || 1,
            duration: existingProtocol.schedule.duration || '',
            durationUnit: existingProtocol.schedule.durationUnit || 'weeks',
            doses: existingProtocol.schedule.doses || []
          });
        } else if (existingProtocol.protocolType === 'weekly' && existingProtocol.schedule) {
          setWeeklySchedule({
            timesPerWeek: existingProtocol.schedule.timesPerWeek || 1,
            duration: existingProtocol.schedule.duration || '',
            durationUnit: existingProtocol.schedule.durationUnit || 'weeks',
            specificDays: existingProtocol.schedule.specificDays || false,
            selectedDays: existingProtocol.schedule.selectedDays || [],
            dosage: existingProtocol.schedule.dosage || '',
            dosageUnit: existingProtocol.schedule.dosageUnit || 'mg',
            instructions: existingProtocol.schedule.instructions || ''
          });
        } else if (existingProtocol.protocolType === 'multiphase' && existingProtocol.schedule) {
          setPhases(existingProtocol.schedule.phases || []);
        }
      }
    }
  }, [isOpen, existingProtocol]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [allMeds, patientProtocols] = await Promise.all([
        ApiService.getMedications(),
        ApiService.getPatientProtocols(patient.patientId)
      ]);
      setMedications(allMeds);
      // Any record on the chart (bare assignment or protocol) marks the
      // medication as assigned to this patient
      setPatientMedications(patientProtocols);
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleMedicationSelect = (medication) => {
    setSelectedMedication(medication);
    
    // Auto-populate dosage unit from medication if available
    const unit = medication.dosageUnit || 'mg';
    setDailySchedule(prev => ({
      ...prev,
      doses: prev.doses.map(dose => ({ ...dose, dosageUnit: unit }))
    }));
    setWeeklySchedule(prev => ({ ...prev, dosageUnit: unit }));
    setPhases(prev => prev.map(phase => ({ ...phase, dosageUnit: unit })));
    
    // Generate default protocol name
    setProtocolName(`${medication.name} Protocol`);
    
    setStep(2);
  };

  const handleProtocolTypeChange = (type) => {
    setProtocolType(type);
    
    // Reset configurations when switching types
    if (type === 'daily' && dailySchedule.doses.length === 0) {
      setDailySchedule(prev => ({
        ...prev,
        doses: [{ time: 'AM', dosage: '', dosageUnit: selectedMedication?.dosageUnit || 'mg', instructions: '' }]
      }));
    }
  };

  // Daily Schedule Handlers
  const updateDailyTimesPerDay = (count) => {
    const newCount = parseInt(count) || 1;
    setDailySchedule(prev => {
      const currentDoses = [...prev.doses];
      
      if (newCount > currentDoses.length) {
        // Add more doses
        const dosesToAdd = newCount - currentDoses.length;
        const timeSlots = ['AM', 'Noon', 'PM', 'Bedtime', 'Morning', 'Afternoon', 'Evening', 'Night'];
        for (let i = 0; i < dosesToAdd; i++) {
          currentDoses.push({
            time: timeSlots[currentDoses.length] || 'AM',
            dosage: '',
            dosageUnit: selectedMedication?.dosageUnit || 'mg',
            instructions: ''
          });
        }
      } else if (newCount < currentDoses.length) {
        // Remove excess doses
        currentDoses.splice(newCount);
      }
      
      return { ...prev, timesPerDay: newCount, doses: currentDoses };
    });
  };

  const updateDailyDose = (index, field, value) => {
    setDailySchedule(prev => ({
      ...prev,
      doses: prev.doses.map((dose, i) => 
        i === index ? { ...dose, [field]: value } : dose
      )
    }));
  };

  // Weekly Schedule Handlers
  const toggleWeekDay = (day) => {
    setWeeklySchedule(prev => ({
      ...prev,
      selectedDays: prev.selectedDays.includes(day)
        ? prev.selectedDays.filter(d => d !== day)
        : [...prev.selectedDays, day]
    }));
  };

  // Multi-Phase Handlers
  const addPhase = () => {
    setPhases(prev => [
      ...prev,
      {
        phaseNumber: prev.length + 1,
        dosage: '',
        dosageUnit: selectedMedication?.dosageUnit || 'mg',
        frequency: 'daily',
        timesPerDay: 1,
        timesPerWeek: 1,
        specificDays: [],
        dailyDoses: [
          {
            time: 'AM',
            dosage: '',
            dosageUnit: selectedMedication?.dosageUnit || 'mg'
          }
        ],
        duration: '',
        durationUnit: 'weeks',
        instructions: ''
      }
    ]);
  };

  const removePhase = (index) => {
    if (phases.length > 1) {
      setPhases(prev => prev.filter((_, i) => i !== index).map((phase, i) => ({
        ...phase,
        phaseNumber: i + 1
      })));
    }
  };

  const updatePhase = (index, field, value) => {
    setPhases(prev => prev.map((phase, i) => 
      i === index ? { ...phase, [field]: value } : phase
    ));
  };

  const updatePhaseTimesPerDay = (phaseIndex, count) => {
    const newCount = parseInt(count) || 1;
    setPhases(prev => prev.map((phase, i) => {
      if (i === phaseIndex) {
        const currentDoses = [...phase.dailyDoses];
        
        if (newCount > currentDoses.length) {
          // Add more doses
          const dosesToAdd = newCount - currentDoses.length;
          const timeSlots = ['AM', 'Noon', 'PM', 'Bedtime', 'Morning', 'Afternoon', 'Evening', 'Night'];
          for (let j = 0; j < dosesToAdd; j++) {
            currentDoses.push({
              time: timeSlots[currentDoses.length] || 'AM',
              dosage: '',
              dosageUnit: phase.dosageUnit || 'mg'
            });
          }
        } else if (newCount < currentDoses.length) {
          // Remove excess doses
          currentDoses.splice(newCount);
        }
        
        return { ...phase, timesPerDay: newCount, dailyDoses: currentDoses };
      }
      return phase;
    }));
  };

  const updatePhaseDailyDose = (phaseIndex, doseIndex, field, value) => {
    setPhases(prev => prev.map((phase, i) => {
      if (i === phaseIndex) {
        return {
          ...phase,
          dailyDoses: phase.dailyDoses.map((dose, j) =>
            j === doseIndex ? { ...dose, [field]: value } : dose
          )
        };
      }
      return phase;
    }));
  };

  const togglePhaseDay = (phaseIndex, day) => {
    setPhases(prev => prev.map((phase, i) => {
      if (i === phaseIndex) {
        return {
          ...phase,
          specificDays: phase.specificDays.includes(day)
            ? phase.specificDays.filter(d => d !== day)
            : [...phase.specificDays, day]
        };
      }
      return phase;
    }));
  };

  const handleSaveProtocol = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      let protocolData = {
        patientId: patient.patientId,
        medicationId: selectedMedication.medicationId,
        medicationName: selectedMedication.name,
        protocolName: protocolName,
        protocolType: protocolType,
        status: existingProtocol?.status || 'active',
        startDate: existingProtocol?.startDate || new Date().toISOString().split('T')[0]
      };

      if (protocolType === 'daily') {
        protocolData.schedule = {
          type: 'daily',
          timesPerDay: dailySchedule.timesPerDay,
          duration: dailySchedule.duration,
          durationUnit: dailySchedule.durationUnit,
          doses: dailySchedule.doses
        };
      } else if (protocolType === 'weekly') {
        protocolData.schedule = {
          type: 'weekly',
          timesPerWeek: weeklySchedule.timesPerWeek,
          specificDays: weeklySchedule.specificDays,
          selectedDays: weeklySchedule.selectedDays,
          duration: weeklySchedule.duration,
          durationUnit: weeklySchedule.durationUnit,
          dosage: weeklySchedule.dosage,
          dosageUnit: weeklySchedule.dosageUnit,
          instructions: weeklySchedule.instructions
        };
      } else if (protocolType === 'multiphase') {
        protocolData.schedule = {
          type: 'multiphase',
          phases: phases
        };
      }

      if (isEditMode) {
        // Update existing protocol
        await ApiService.updatePatientProtocol(
          patient.patientId,
          existingProtocol.patientProtocolId,
          protocolData
        );
      } else {
        // Create new protocol
        await ApiService.createPatientProtocol(patient.patientId, protocolData);
      }
      
      onProtocolAssigned();
      handleClose();
    } catch (err) {
      setError(isEditMode ? 'Failed to update protocol' : 'Failed to create protocol');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setStep(existingProtocol ? 2 : 1);
    if (!existingProtocol) {
      setSelectedMedication(null);
    }
    setProtocolType('daily');
    setProtocolName('');
    setDailySchedule({
      timesPerDay: 1,
      duration: '',
      durationUnit: 'weeks',
      doses: [{ time: 'AM', dosage: '', dosageUnit: 'mg', instructions: '' }]
    });
    setWeeklySchedule({
      timesPerWeek: 1,
      duration: '',
      durationUnit: 'weeks',
      specificDays: false,
      selectedDays: [],
      dosage: '',
      dosageUnit: 'mg',
      instructions: ''
    });
    setPhases([{
      phaseNumber: 1,
      dosage: '',
      dosageUnit: 'mg',
      frequency: 'daily',
      timesPerDay: 1,
      timesPerWeek: 1,
      specificDays: [],
      duration: '',
      durationUnit: 'weeks',
      instructions: ''
    }]);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  // Check permissions
  const canAdd = hasPermission(PERMISSIONS.PATIENTS_PROTOCOLS_ADD);
  const canEdit = hasPermission(PERMISSIONS.PATIENTS_PROTOCOLS_EDIT);
  
  if (isEditMode && !canEdit) {
    return null; // Don't show modal if editing without permission
  }
  
  if (!isEditMode && !canAdd) {
    return null; // Don't show modal if adding without permission
  }

  // Filter to only show medications assigned to this patient
  const availableMedications = medications.filter(med => 
    patientMedications.some(pm => pm.medicationId === med.medicationId)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header - Sticky at top */}
        <div className="bg-spruce-800 px-6 py-4 flex items-center justify-between rounded-t-xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-lg">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {isEditMode ? 'Edit' : 'Create'} {t.protocol.singular}
              </h2>
              <p className="text-spruce-100 text-sm">
                {patient.firstName} {patient.lastName}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="overflow-y-auto flex-1">
        <form id="protocol-form" onSubmit={handleSaveProtocol} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Step Indicator */}
          <div className="mb-6 flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-spruce-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 1 ? 'bg-spruce-600 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className="font-medium">Select {t.item.singular}</span>
            </div>
            <div className="flex-1 h-0.5 bg-gray-200"></div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-spruce-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${step >= 2 ? 'bg-spruce-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="font-medium">Configure {t.protocol.singular}</span>
            </div>
          </div>

          {/* Step 1: Select Medication */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Select {t.item.singular} <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Choose from {t.item.pluralLower} assigned to this {t.protocol.singularLower}
                </p>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader className="w-6 h-6 text-spruce-600 animate-spin" />
                  </div>
                ) : availableMedications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    No {t.item.pluralLower} assigned to this patient yet.
                    <br />
                    Please assign a {t.item.singularLower} first.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {availableMedications.map((med) => (
                      <button
                        key={med.medicationId}
                        type="button"
                        onClick={() => handleMedicationSelect(med)}
                        className="p-4 border-2 border-gray-200 rounded-lg hover:border-spruce-500 hover:bg-spruce-50 transition-all text-left"
                      >
                        <div className="font-semibold text-gray-900">{med.name}</div>
                        {med.category && (
                          <span className="inline-block mt-1 px-2 py-0.5 bg-spruce-100 text-spruce-700 text-xs rounded-full">
                            {med.category}
                          </span>
                        )}
                        {med.dosageUnit && (
                          <div className="text-sm text-gray-500 mt-1">
                            Measured in {med.dosageUnit}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Configure Protocol */}
          {step === 2 && selectedMedication && (
            <div className="space-y-6">
              {/* Selected Medication Display */}
              <div className="bg-spruce-50 border border-spruce-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-spruce-700 font-medium">Selected {t.item.singular}</p>
                    <p className="text-lg font-bold text-spruce-900">{selectedMedication.name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-sm text-spruce-600 hover:text-spruce-800 underline"
                  >
                    Change
                  </button>
                </div>
              </div>

              {/* Protocol Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {t.protocol.singular} Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={protocolName}
                  onChange={(e) => setProtocolName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                  placeholder={`e.g., ${selectedMedication.name} Standard Protocol`}
                  required
                />
              </div>

              {/* Protocol Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  {t.protocol.singular} Type <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => handleProtocolTypeChange('daily')}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      protocolType === 'daily'
                        ? 'border-spruce-500 bg-spruce-50 text-spruce-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold">Daily Schedule</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Multiple times per day
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleProtocolTypeChange('weekly')}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      protocolType === 'weekly'
                        ? 'border-spruce-500 bg-spruce-50 text-spruce-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold">Weekly Schedule</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Specific days of week
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleProtocolTypeChange('multiphase')}
                    className={`p-4 border-2 rounded-lg transition-all ${
                      protocolType === 'multiphase'
                        ? 'border-spruce-500 bg-spruce-50 text-spruce-900'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-semibold">Multi-Phase</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Dosage escalation
                    </div>
                  </button>
                </div>
              </div>

              {/* Daily Schedule Configuration */}
              {protocolType === 'daily' && (
                <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="font-semibold text-gray-900">Daily Schedule Configuration</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Times per Day <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={dailySchedule.timesPerDay}
                        onChange={(e) => updateDailyTimesPerDay(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Duration (Optional)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          value={dailySchedule.duration}
                          onChange={(e) => setDailySchedule(prev => ({ ...prev, duration: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                          placeholder="4"
                        />
                        <select
                          value={dailySchedule.durationUnit}
                          onChange={(e) => setDailySchedule(prev => ({ ...prev, durationUnit: e.target.value }))}
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                        >
                          <option value="days">Days</option>
                          <option value="weeks">Weeks</option>
                          <option value="months">Months</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Individual Doses */}
                  <div className="space-y-3 mt-4">
                    <label className="block text-sm font-semibold text-gray-700">
                      Configure Each Dose
                    </label>
                    {dailySchedule.doses.map((dose, index) => (
                      <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="font-medium text-gray-700 mb-3">Dose {index + 1}</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Time</label>
                            <select
                              value={dose.time}
                              onChange={(e) => updateDailyDose(index, 'time', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                            >
                              {timeOptions.map(time => (
                                <option key={time} value={time}>{time}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Dosage</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={dose.dosage}
                                onChange={(e) => updateDailyDose(index, 'dosage', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                                placeholder="6"
                                required
                              />
                              <span className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                                {dose.dosageUnit}
                              </span>
                            </div>
                          </div>

                          <div className="col-span-2">
                            <label className="block text-sm text-gray-600 mb-1">Instructions</label>
                            <input
                              type="text"
                              value={dose.instructions}
                              onChange={(e) => updateDailyDose(index, 'instructions', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                              placeholder="with Food, on empty stomach, etc."
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Weekly Schedule Configuration */}
              {protocolType === 'weekly' && (
                <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="font-semibold text-gray-900">Weekly Schedule Configuration</h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Times per Week <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="7"
                        value={weeklySchedule.timesPerWeek}
                        onChange={(e) => {
                          const value = parseInt(e.target.value);
                          if (!isNaN(value) && value >= 1 && value <= 7) {
                            setWeeklySchedule(prev => ({ ...prev, timesPerWeek: value }));
                          } else if (e.target.value === '') {
                            setWeeklySchedule(prev => ({ ...prev, timesPerWeek: '' }));
                          }
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '') {
                            setWeeklySchedule(prev => ({ ...prev, timesPerWeek: 1 }));
                          }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Duration (Optional)
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="number"
                          min="1"
                          value={weeklySchedule.duration}
                          onChange={(e) => setWeeklySchedule(prev => ({ ...prev, duration: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                          placeholder="8"
                        />
                        <select
                          value={weeklySchedule.durationUnit}
                          onChange={(e) => setWeeklySchedule(prev => ({ ...prev, durationUnit: e.target.value }))}
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                        >
                          <option value="weeks">Weeks</option>
                          <option value="months">Months</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Specific Days Toggle */}
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={weeklySchedule.specificDays}
                        onChange={(e) => setWeeklySchedule(prev => ({ ...prev, specificDays: e.target.checked }))}
                        className="w-4 h-4 text-spruce-600 rounded focus:ring-spruce-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Specify which days of the week
                      </span>
                    </label>
                  </div>

                  {/* Days Selection */}
                  {weeklySchedule.specificDays && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Select Days <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {daysOfWeek.map(day => (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleWeekDay(day)}
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${
                              weeklySchedule.selectedDays.includes(day)
                                ? 'bg-spruce-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:border-spruce-300'
                            }`}
                          >
                            {day.substring(0, 3)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dosage and Instructions */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Dosage <span className="text-red-500">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={weeklySchedule.dosage}
                          onChange={(e) => setWeeklySchedule(prev => ({ ...prev, dosage: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                          placeholder="3"
                          required
                        />
                        <span className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                          {weeklySchedule.dosageUnit}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Instructions
                      </label>
                      <input
                        type="text"
                        value={weeklySchedule.instructions}
                        onChange={(e) => setWeeklySchedule(prev => ({ ...prev, instructions: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                        placeholder="with Food"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Multi-Phase Configuration */}
              {protocolType === 'multiphase' && (
                <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Multi-Phase Configuration</h3>
                    <button
                      type="button"
                      onClick={addPhase}
                      className="flex items-center gap-2 px-3 py-1.5 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 text-sm"
                    >
                      <Plus size={16} />
                      Add Phase
                    </button>
                  </div>

                  {phases.map((phase, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-medium text-gray-700">Phase {phase.phaseNumber}</div>
                        {phases.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePhase(index)}
                            className="text-red-600 hover:bg-red-50 p-1 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>

                      <div className="space-y-3">
                        {/* Dosage */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Dosage *</label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={phase.dosage}
                                onChange={(e) => updatePhase(index, 'dosage', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                                placeholder="2"
                                required
                              />
                              <span className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
                                {phase.dosageUnit}
                              </span>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Frequency *</label>
                            <select
                              value={phase.frequency}
                              onChange={(e) => updatePhase(index, 'frequency', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                            >
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                            </select>
                          </div>
                        </div>

                        {/* Frequency Details */}
                        {phase.frequency === 'daily' && (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Times per Day *</label>
                              <input
                                type="number"
                                min="1"
                                max="10"
                                value={phase.timesPerDay}
                                onChange={(e) => updatePhaseTimesPerDay(index, e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                                required
                              />
                            </div>

                            {/* Individual Doses for this Phase */}
                            <div className="space-y-2">
                              <label className="block text-sm text-gray-600 font-medium">Configure Each Dose</label>
                              {phase.dailyDoses.map((dose, doseIndex) => (
                                <div key={doseIndex} className="bg-gray-50 border border-gray-200 rounded p-3">
                                  <div className="text-xs text-gray-500 mb-2">Dose {doseIndex + 1}</div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div>
                                      <label className="block text-xs text-gray-600 mb-1">Time</label>
                                      <select
                                        value={dose.time}
                                        onChange={(e) => updatePhaseDailyDose(index, doseIndex, 'time', e.target.value)}
                                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-spruce-500"
                                      >
                                        {timeOptions.map(time => (
                                          <option key={time} value={time}>{time}</option>
                                        ))}
                                      </select>
                                    </div>

                                    <div>
                                      <label className="block text-xs text-gray-600 mb-1">Dosage</label>
                                      <div className="flex gap-1">
                                        <input
                                          type="text"
                                          value={dose.dosage}
                                          onChange={(e) => updatePhaseDailyDose(index, doseIndex, 'dosage', e.target.value)}
                                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-spruce-500"
                                          placeholder="6"
                                        />
                                        <span className="px-2 py-1.5 text-sm bg-gray-100 border border-gray-300 rounded text-gray-700">
                                          {dose.dosageUnit}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {phase.frequency === 'weekly' && (
                          <div className="space-y-2">
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Times per Week *</label>
                              <input
                                type="number"
                                min="1"
                                max="7"
                                value={phase.timesPerWeek}
                                onChange={(e) => {
                                  const value = parseInt(e.target.value);
                                  if (!isNaN(value) && value >= 1 && value <= 7) {
                                    updatePhase(index, 'timesPerWeek', value);
                                  } else if (e.target.value === '') {
                                    updatePhase(index, 'timesPerWeek', '');
                                  }
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    updatePhase(index, 'timesPerWeek', 1);
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-600 mb-1">Specific Days (Optional)</label>
                              <div className="flex flex-wrap gap-2">
                                {daysOfWeek.map(day => (
                                  <button
                                    key={day}
                                    type="button"
                                    onClick={() => togglePhaseDay(index, day)}
                                    className={`px-3 py-1 rounded text-sm font-medium transition-all ${
                                      phase.specificDays.includes(day)
                                        ? 'bg-spruce-600 text-white'
                                        : 'bg-white border border-gray-300 text-gray-700 hover:border-spruce-300'
                                    }`}
                                  >
                                    {day.substring(0, 3)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Duration */}
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Duration *</label>
                            <input
                              type="number"
                              min="1"
                              value={phase.duration}
                              onChange={(e) => updatePhase(index, 'duration', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                              placeholder="2"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">Unit</label>
                            <select
                              value={phase.durationUnit}
                              onChange={(e) => updatePhase(index, 'durationUnit', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                            >
                              <option value="days">Days</option>
                              <option value="weeks">Weeks</option>
                              <option value="months">Months</option>
                            </select>
                          </div>
                        </div>

                        {/* Instructions */}
                        <div>
                          <label className="block text-sm text-gray-600 mb-1">Instructions</label>
                          <input
                            type="text"
                            value={phase.instructions}
                            onChange={(e) => updatePhase(index, 'instructions', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-spruce-500"
                            placeholder="with Food, on empty stomach, etc."
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </form>
        </div>

        {/* Footer - Sticky at bottom */}
        <div className="flex gap-3 justify-end px-6 py-4 border-t border-gray-200 bg-white rounded-b-xl flex-shrink-0">
          <button
            type="button"
            onClick={handleClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
          >
            Cancel
          </button>
          {step === 2 && (
            <button
              type="submit"
              form="protocol-form"
              disabled={saving}
              className="px-6 py-2 bg-spruce-600 text-white rounded-lg hover:bg-spruce-700 font-medium transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  {isEditMode ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? 'Update' : 'Create'} {t.protocol.singular}
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Error Modal */}
      {errorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="text-red-600" size={24} />
              </div>
              <h3 className="text-xl font-bold text-gray-800">{errorModal.title}</h3>
            </div>
            <p className="text-gray-600 mb-6">
              {errorModal.message}
            </p>
            <button
              onClick={() => setErrorModal(null)}
              className="w-full bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProtocolAssignment;